import { inject, Injectable } from '@angular/core';
import { LogEntry, LogLevel, NetworkLogData } from './logging.model';
import { Stage } from '../enums';
import { LOGGING_CONFIG, MAX_LOG_ENTRIES, STORAGE_KEY } from './logging.config';

@Injectable({ providedIn: 'root' })
export class LoggingService {
    private readonly config = inject(LOGGING_CONFIG);
    private readonly buffer: LogEntry[] = this.loadFromStorage();

    private get consoleEnabled(): boolean {
        return [Stage.LOCAL, Stage.DEV, Stage.QA, Stage.TEST].includes(this.config.stage);
    }

    public debug(message: string, data?: unknown): void {
        this.log('debug', message, data);
    }

    public info(message: string, data?: unknown): void {
        this.log('info', message, data);
    }

    public warn(message: string, data?: unknown): void {
        this.log('warn', message, data);
    }

    public error(message: string, data?: unknown): void {
        this.log('error', message, data);
    }

    public network(message: string, data: NetworkLogData): void {
        this.log('network', message, data);
    }

    public getEntries(): ReadonlyArray<LogEntry> {
        return [...this.buffer];
    }

    public downloadLogs(): void {
        const json = JSON.stringify(this.buffer, null, 2);
        const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mdla-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }

    private log(level: LogLevel, message: string, data?: unknown): void {
        const entry: LogEntry = {
            timestamp: new Date().toISOString(),
            level,
            message,
            ...(data !== undefined && { data })
        };

        this.buffer.push(entry);
        if (this.buffer.length > MAX_LOG_ENTRIES) {
            this.buffer.shift();
        }

        this.saveToStorage();

        if (this.consoleEnabled) {
            this.writeToConsole(entry);
        }
    }

    private saveToStorage(): void {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.buffer));
        } catch {
            // Storage full or unavailable — continue without persistence
        }
    }

    private loadFromStorage(): LogEntry[] {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed.slice(-MAX_LOG_ENTRIES);
                }
            }
        } catch {
            // Corrupted or unavailable — start fresh
        }
        return [];
    }

    private writeToConsole(entry: LogEntry): void {
        const args: unknown[] = entry.data !== undefined ? [entry.message, entry.data] : [entry.message];

        switch (entry.level) {
            case 'debug':
                console.log(`%c[DEBUG]`, 'color:#a6adc8', ...args);
                break;
            case 'info':
                console.log(`%c[INFO]`, 'color:#89b4fa', ...args);
                break;
            case 'warn':
                console.warn(`[WARN]`, ...args);
                break;
            case 'error':
                console.error(`[ERROR]`, ...args);
                break;
            case 'network':
                console.groupCollapsed(
                    `%c[NETWORK] %c${entry.message}`,
                    'color:#a6e3a1;font-weight:bold',
                    'color:inherit'
                );
                if (entry.data) {
                    console.table(
                        Object.fromEntries(
                            Object.entries(entry.data as Record<string, unknown>).filter(
                                ([key]) => !['payload', 'response', 'error'].includes(key)
                            )
                        )
                    );
                    const nd = entry.data as NetworkLogData;
                    if (nd.payload) {
                        console.log('Payload:', nd.payload);
                    }
                    if (nd.response) {
                        console.log('Response:', nd.response);
                    }
                    if (nd.error) {
                        console.error('Error:', nd.error);
                    }
                }
                console.groupEnd();
                break;
        }
    }
}
